"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FlaskConical,
  Hourglass,
  Loader2,
  Send,
  XCircle,
} from "lucide-react";
import {
  sendTestWebhook,
  type DeliveryAttempt,
  type DeliveryDetail,
  type WebhookEndpoint,
  type WebhookEventType,
} from "../../../services/merchantApi";
import { CodeBlock, CopyButton, DevModal, InlineNotice, focusRing, ghostBtn, inputCls, primaryBtn } from "../ui";
import { formatBytes, formatCount, formatDuration, timeAgo } from "./format";
import { HttpStatusChip } from "./HttpStatusChip";
import { EVENT_TYPE_LABEL, fallbackHint } from "./vocab";
import { apiErrorOf, useCountdown, useEventTypes } from "./EndpointFormModal";
import { notifyWebhooksChanged } from "./useWebhookSummary";

// ────────────────────────────────────────────────────────────────────────────
// SendTestModal — send one signed test event to an endpoint and show what
// the merchant's server answered, live, in the dialog.
//
// The request is synchronous on our side (up to ~16 s: DNS + HTTP + body);
// the client gives up at 25 s. Test events have livemode:false, work on
// paused endpoints and never touch health or alert emails.
//
// Everything in the result panel that came from the merchant's server
// (status text, response body, error message) is rendered as plain text.
// ────────────────────────────────────────────────────────────────────────────

const SLOW_AFTER_S = 20;
const LOW_QUOTA = 10;

export interface SendTestModalProps {
  open: boolean;
  onClose: () => void;
  wallet: string | null;
  email: string | null;
  endpoint: WebhookEndpoint;
  /** A test delivery was created (the Deliveries tab should refetch). */
  onSent?: () => void;
}

type Outcome =
  | { kind: "result"; delivery: DeliveryDetail }
  | { kind: "timeout"; message: string }
  | { kind: "error"; tone: "error" | "warn"; title: string; message: string; requestId?: string | null };

/** Renders nothing while closed, so every opening starts fresh. */
export default function SendTestModal(props: SendTestModalProps) {
  if (!props.open) return null;
  return <SendTestDialog {...props} />;
}

function SendTestDialog({ onClose, wallet, email, endpoint, onSent }: SendTestModalProps) {
  const { types } = useEventTypes(wallet, email);
  const [eventType, setEventType] = useState<WebhookEventType>("checkout.session.completed");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [quota, setQuota] = useState<{ remaining: number; resetsAt: string | null } | null>(null);
  const cooldown = useCountdown();
  const resultRef = useRef<HTMLDivElement>(null);
  const selectId = useId();
  const previewId = useId();

  const current = types.find((t) => t.type === eventType) || types[0];
  const preview = useMemo(
    () => (current?.samplePayload ? JSON.stringify(current.samplePayload, null, 2) : null),
    [current],
  );

  // Elapsed seconds while the request is on the wire.
  useEffect(() => {
    if (!sending) return;
    const started = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 500);
    return () => clearInterval(id);
  }, [sending]);

  // The result panel takes focus as soon as it appears.
  useEffect(() => {
    if (outcome) resultRef.current?.focus();
  }, [outcome]);

  const send = async () => {
    if (sendingRef.current || cooldown.active) return;
    sendingRef.current = true;
    setSending(true);
    setOutcome(null);
    try {
      const res = await sendTestWebhook(wallet, email, endpoint.id, eventType);
      setOutcome({ kind: "result", delivery: res.delivery });
      if (typeof res.testsRemaining === "number") {
        setQuota({ remaining: res.testsRemaining, resetsAt: res.windowResetsAt || null });
      }
      onSent?.();
    } catch (err) {
      const e = apiErrorOf(err);
      if (e.code === "client_timeout") {
        // We stopped waiting; the attempt itself still finishes server-side.
        setOutcome({ kind: "timeout", message: e.message });
        onSent?.();
      } else if (e.status === 429) {
        cooldown.start(e.retryAfter);
        setOutcome({
          kind: "error",
          tone: "warn",
          title: "Too many test sends",
          message: e.message || "You've sent a lot of test events in a short time.",
        });
      } else if (e.code === "webhooks_unavailable") {
        setOutcome({
          kind: "error",
          tone: "warn",
          title: "Test sending is paused on our side",
          message:
            "We can't sign test events right now. Live deliveries are queued and nothing is lost — please try again in a few minutes.",
        });
      } else {
        setOutcome({
          kind: "error",
          tone: "error",
          title: "Couldn't send the test event",
          message: e.message || "Something went wrong. Please try again.",
          requestId: e.requestId,
        });
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
      notifyWebhooksChanged();
    }
  };

  let buttonBody: ReactNode;
  if (sending) {
    buttonBody = (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        Sending…{elapsed >= 2 && <span className="tabular-nums font-normal opacity-80">{elapsed}s</span>}
      </>
    );
  } else if (cooldown.active) {
    buttonBody = (
      <>
        <Clock className="w-4 h-4" />
        <span className="tabular-nums">{cooldown.text}</span>
      </>
    );
  } else {
    buttonBody = (
      <>
        <Send className="w-4 h-4" />
        {outcome ? "Send again" : "Send test event"}
      </>
    );
  }

  return (
    <DevModal
      open
      onClose={onClose}
      title="Send a test event"
      icon={<FlaskConical className="w-5 h-5" />}
      size="2xl"
      busy={sending}
    >
      <p className="text-sm text-gray-400 -mt-1">
        One signed POST to{" "}
        <span className="font-mono text-gray-200 break-all">{endpoint.url}</span>
      </p>

      <div className="mt-5">
        <label htmlFor={selectId} className="block text-[11px] uppercase tracking-[0.16em] text-gray-500 font-semibold mb-1.5">
          Event type
        </label>
        <div className="relative">
          <select
            id={selectId}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as WebhookEventType)}
            disabled={sending}
            className={`${inputCls} font-mono appearance-none pr-10 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70`}
          >
            {types.map((t) => (
              <option key={t.type} value={t.type} className="bg-gray-900">
                {t.type}
              </option>
            ))}
          </select>
          <ChevronDown
            className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          />
        </div>
        {current && (
          <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">
            <span className="text-gray-300">{EVENT_TYPE_LABEL[current.type] || current.type}.</span>{" "}
            {current.description}
          </p>
        )}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setPreviewOpen((v) => !v)}
          aria-expanded={previewOpen}
          aria-controls={previewId}
          className={`inline-flex items-center gap-1.5 py-1.5 text-xs font-medium text-gray-400 hover:text-white rounded-md ${focusRing}`}
        >
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform ${previewOpen ? "rotate-90" : ""}`}
            aria-hidden="true"
          />
          Payload preview
        </button>
        <div id={previewId} hidden={!previewOpen}>
          {previewOpen &&
            (preview ? (
              <CodeBlock language="json" code={preview} highlight wrap collapsedMaxLines={18} />
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                The sample payload couldn't be loaded. The test event has the same shape as a live{" "}
                <span className="font-mono">{eventType}</span> event.
              </p>
            ))}
        </div>
      </div>

      <InlineNotice icon={FlaskConical} className="mt-4">
        Test events have <span className="font-mono text-gray-300">livemode:false</span> and a{" "}
        <span className="font-mono text-gray-300">cs_test_</span> session id. They work on paused endpoints
        and never affect endpoint health or send alert emails.
      </InlineNotice>

      {/* Live region stays mounted (and never display:none) so the arrival of
          a result is announced. */}
      <div role="status" aria-live="polite">
        {outcome && (
          <div ref={resultRef} tabIndex={-1} className="mt-5 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white/25">
            <ResultPanel outcome={outcome} endpoint={endpoint} />
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
        <button type="button" onClick={onClose} disabled={sending} className={`${ghostBtn} w-full sm:w-auto`}>
          {outcome ? "Done" : "Cancel"}
        </button>
        <button
          type="button"
          onClick={send}
          disabled={sending || cooldown.active}
          className={`${primaryBtn} w-full sm:w-auto justify-center disabled:cursor-not-allowed`}
        >
          {buttonBody}
        </button>
      </div>

      {sending && elapsed >= SLOW_AFTER_S && (
        <p className="mt-2 text-xs text-amber-300/90 sm:text-right">
          Still waiting for your server… (up to 15 seconds)
        </p>
      )}
      {!sending && quota && quota.remaining <= LOW_QUOTA && (
        <p className="mt-2 text-xs text-gray-500 sm:text-right tabular-nums">
          {quota.remaining <= 0
            ? `No test sends left for this endpoint this hour${
                quota.resetsAt ? ` — more ${timeAgo(quota.resetsAt)}` : ""
              }.`
            : `${formatCount(quota.remaining)} test ${quota.remaining === 1 ? "send" : "sends"} left this hour`}
        </p>
      )}
    </DevModal>
  );
}

// ─── Result panel ──────────────────────────────────────────────────────────

function headerValue(headers: Record<string, string> | null | undefined, name: string): string | null {
  if (!headers) return null;
  const want = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === want && typeof v === "string") return v;
  }
  return null;
}

function ResultPanel({ outcome, endpoint }: { outcome: Outcome; endpoint: WebhookEndpoint }) {
  if (outcome.kind === "timeout") {
    return (
      <InlineNotice
        tone="warn"
        icon={Hourglass}
        title="No answer yet"
        action={
          <Link
            to={`/developers/webhooks?tab=deliveries&mode=test&endpoint=${encodeURIComponent(endpoint.id)}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-white"
          >
            Open test deliveries <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        }
      >
        {outcome.message}
      </InlineNotice>
    );
  }
  if (outcome.kind === "error") {
    return (
      <InlineNotice tone={outcome.tone} title={outcome.title}>
        {outcome.message}
        {outcome.requestId && (
          <span className="block mt-1 text-[11px] font-mono opacity-70 break-all">
            Request ID · {outcome.requestId}
          </span>
        )}
      </InlineNotice>
    );
  }
  // Keyed by delivery so "Response body" starts collapsed for every send.
  return <DeliveryResult key={outcome.delivery.id} delivery={outcome.delivery} endpoint={endpoint} />;
}

function DeliveryResult({ delivery: d, endpoint }: { delivery: DeliveryDetail; endpoint: WebhookEndpoint }) {
  const [bodyOpen, setBodyOpen] = useState(false);
  const bodyId = useId();
  const attempts: DeliveryAttempt[] = Array.isArray(d.attempts) ? d.attempts : [];
  const a = attempts.length ? attempts[attempts.length - 1] : null;

  const ok = d.status === "succeeded";
  const failed = d.status === "failed";
  const status = a?.responseStatus ?? d.lastResponseStatus ?? null;
  const code = a?.errorCode ?? d.lastErrorCode ?? null;
  const duration = a?.durationMs ?? d.lastDurationMs ?? null;
  const message = a?.errorMessage ?? d.lastErrorMessage ?? null;
  const hint = failed ? a?.hint ?? fallbackHint(code, status) : null;
  const signature = headerValue(d.request?.headers, "X-CashTap-Signature") ?? a?.signature ?? null;
  const snippet = a?.responseSnippet ?? null;
  const gotResponse = typeof status === "number" && status > 0;
  const detailHref = `/developers/webhooks/deliveries/${encodeURIComponent(d.id)}`;

  const tone = ok
    ? { box: "border-[#61CE70]/30 bg-[#61CE70]/5", title: "text-white", icon: "text-[#61CE70]" }
    : failed
      ? { box: "border-red-500/30 bg-red-500/5", title: "text-red-200", icon: "text-red-400" }
      : { box: "border-gray-800/80 bg-gray-900/30", title: "text-gray-100", icon: "text-gray-400" };
  const Icon = ok ? CheckCircle2 : failed ? XCircle : Clock;
  const label = ok ? "Delivered" : failed ? "Attempt failed" : "Queued";

  return (
    <div className={`rounded-xl border p-4 ${tone.box}`}>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <Icon className={`w-5 h-5 shrink-0 ${tone.icon}`} aria-hidden="true" />
        <p className={`font-semibold ${tone.title}`}>{label}</p>
        <HttpStatusChip status={status} errorCode={failed ? code : null} />
        {duration !== null && (
          <span className="text-xs text-gray-400 tabular-nums">{formatDuration(duration)}</span>
        )}
      </div>

      {ok && (
        <p className="mt-2 text-sm text-gray-300">
          Your server acknowledged the test event with a 2xx response.
          {endpoint.enabled && endpoint.health === "failing" && (
            <>
              {" "}
              Deliveries that are still waiting retry automatically on their schedule — open one and use{" "}
              <span className="text-white font-medium">Retry now</span> to send it immediately.
            </>
          )}
        </p>
      )}
      {!ok && !failed && (
        <p className="mt-2 text-sm text-gray-300">
          The test is queued on our side and will be sent within a few seconds. Its result appears in the
          delivery history.
        </p>
      )}
      {failed && message && <p className="mt-2 text-sm text-red-100/90 break-words">{message}</p>}
      {hint && <p className="mt-1.5 text-xs text-gray-400 leading-relaxed break-words">{hint}</p>}

      <dl className="mt-4 space-y-2.5 text-xs">
        {signature && (
          <div className="sm:flex sm:gap-4">
            <dt className="text-gray-500 sm:w-32 shrink-0">Signature sent</dt>
            <dd className="min-w-0 mt-0.5 sm:mt-0">
              <span className="font-mono text-gray-300 break-all">X-CashTap-Signature: {signature}</span>
              <span className="block text-[11px] text-gray-600 mt-0.5">
                Shortened for display — your server received the full value.
              </span>
            </dd>
          </div>
        )}
        <div className="sm:flex sm:gap-4">
          <dt className="text-gray-500 sm:w-32 shrink-0">Sent to</dt>
          <dd className="min-w-0 mt-0.5 sm:mt-0 font-mono text-gray-300 break-all">
            {a?.url || d.endpointUrl || endpoint.url}
            {a?.resolvedIp && <span className="text-gray-500"> ({a.resolvedIp})</span>}
          </dd>
        </div>
        <div className="sm:flex sm:gap-4">
          <dt className="text-gray-500 sm:w-32 shrink-0">Event id</dt>
          <dd className="min-w-0 mt-0.5 sm:mt-0 flex items-center gap-1">
            <span className="font-mono text-gray-300 break-all">{d.eventId}</span>
            <CopyButton text={d.eventId} label="Copy event id" className="-my-2" />
          </dd>
        </div>
      </dl>

      {gotResponse && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setBodyOpen((v) => !v)}
            aria-expanded={bodyOpen}
            aria-controls={bodyId}
            className={`inline-flex items-center gap-1.5 py-1.5 text-xs font-medium text-gray-400 hover:text-white rounded-md ${focusRing}`}
          >
            <ChevronRight
              className={`w-3.5 h-3.5 transition-transform ${bodyOpen ? "rotate-90" : ""}`}
              aria-hidden="true"
            />
            Response body
            {snippet ? (
              <span className="text-gray-600 tabular-nums">
                ({formatBytes(new Blob([snippet]).size)}
                {a?.responseTruncated ? "+" : ""})
              </span>
            ) : (
              <span className="text-gray-600">(empty)</span>
            )}
          </button>
          <div id={bodyId} hidden={!bodyOpen}>
            {bodyOpen &&
              (snippet ? (
                <>
                  <CodeBlock language="response" code={snippet} wrap highlight collapsedMaxLines={14} />
                  <p className="text-[11px] text-gray-500 -mt-1">
                    First 2,000 characters of your server's response.
                    {a?.responseTruncated && " (truncated)"}
                  </p>
                </>
              ) : (
                <p className="mt-1 text-xs text-gray-500">Your server sent no response body.</p>
              ))}
          </div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-white/5">
        <Link
          to={detailHref}
          className={`inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-[#61CE70] hover:text-white rounded-sm ${focusRing}`}
        >
          View full delivery <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
